import { useEffect } from 'react';
import L from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { translateRoadCatalog } from '../../damages/roadDamageTranslations';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const pinIcon = (color: string) =>
  L.divIcon({
    className: 'road-pin',
    html: `<span style="background:${color}"></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
    popupAnchor: [0, -18],
  });

const openPin = pinIcon('#d92d20');
const fixedPin = pinIcon('#1f8a4c');

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export type MappedRoadDamage = {
  id: number;
  roadDamageNumber: number;
  roadDamageType: string;
  roadElement: string;
  severity: string;
  roadLatitude: number | null;
  roadLongitude: number | null;
  locationAccuracy?: number | null;
  registeredAt: string;
  createdAt: string;
  roadImageData: string;
  roadRemediated?: boolean | null;
};

type Props = {
  damages: MappedRoadDamage[];
  onClose: () => void;
};

const FitDamageBounds = ({ damages }: { damages: MappedRoadDamage[] }) => {
  const map = useMap();
  useEffect(() => {
    if (!damages.length) return;
    const bounds = L.latLngBounds(
      damages.map((damage) => [Number(damage.roadLatitude), Number(damage.roadLongitude)])
    );
    map.fitBounds(bounds, { padding: [35, 35], maxZoom: 18 });
  }, [damages, map]);
  return null;
};

export const RoadDamagesMap = ({ damages, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const located = damages.filter(
    (damage) =>
      Number.isFinite(Number(damage.roadLatitude)) &&
      Number.isFinite(Number(damage.roadLongitude))
  );
  const center: [number, number] = located.length
    ? [Number(located[0].roadLatitude), Number(located[0].roadLongitude)]
    : [40.4168, -3.7038];

  return (
    <div className="road-modal-backdrop road-map-backdrop" onClick={onClose}>
      <div className="road-map-modal" onClick={(event) => event.stopPropagation()}>
        <div className="road-modal-head">
          <h3>{t('roadDamages.mapTitle')}</h3>
          <button type="button" className="road-close" onClick={onClose}>
            ×
          </button>
        </div>
        {located.length === 0 ? (
          <p className="road-empty">{t('roadDamages.noMappedDamages')}</p>
        ) : (
          <MapContainer center={center} zoom={16} className="road-map">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitDamageBounds damages={located} />
            {located.map((damage) => {
              const id = String(damage.roadDamageNumber).padStart(4, '0');
              const type = translateRoadCatalog(i18n.language, damage.roadDamageType);
              const fixed = Boolean(damage.roadRemediated);
              return (
                <Marker
                  key={damage.id}
                  position={[Number(damage.roadLatitude), Number(damage.roadLongitude)]}
                  icon={fixed ? fixedPin : openPin}
                >
                  <Tooltip>
                    <strong>ID:{id}</strong>
                    <br />
                    {type} · {damage.severity}
                    <br />
                    {fixed ? t('roadDamages.damageFixed') : t('roadDamages.damageOpen')}
                  </Tooltip>
                  <Popup>
                    <div className="road-map-popup">
                      <img src={damage.roadImageData} alt={type} />
                      <strong>ID:{id} · {type}</strong>
                      <span>{t('roadDamages.element')}: {translateRoadCatalog(i18n.language, damage.roadElement)}</span>
                      <span>{t('roadDamages.severity')}: {damage.severity}</span>
                      <span>
                        {Number(damage.roadLatitude).toFixed(6)},{' '}
                        {Number(damage.roadLongitude).toFixed(6)}
                      </span>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>
    </div>
  );
};
